import { __, compose, contains, prop } from 'ramda';
import { Observable, of } from 'rxjs';
import { filter, mergeMap, withLatestFrom } from 'rxjs/operators';
import { WebAnalyticsContext } from '@bridebook/web/lib/bbcommon/utils/bridebook-analytics';
import { Action, IApplicationState, IEpicDeps } from '@bridebook/web/lib/types';

export const datepickerAnalyticsEpics = (action$: Observable<Action>, { state$ }: IEpicDeps) =>
  action$.pipe(
    filter(
      compose(
        contains(__, [
          'START_DATEPICKER_DATE_EDIT',
          'CANCEL_DATEPICKER_EDIT',
          'DATEPICKER_CHANGE_TAB_SUCCESS',
          'CHANGE_DATEPICKER_DATE',
        ]),
        prop('type'),
      ),
    ),
    withLatestFrom(state$),
    mergeMap(([action, state]: [Action, IApplicationState]) => {
      const { type, payload } = action;

      switch (type) {
        case 'START_DATEPICKER_DATE_EDIT':
        case 'CANCEL_DATEPICKER_EDIT': {
          const { track = true } = payload;
          if (!track) {
            return of();
          }
          return of({
            type: 'TOGGLED_DATEPICKER_ANALYTICS',
            payload: {
              ...payload,
              datepickerToggledStatus: type === 'START_DATEPICKER_DATE_EDIT',
            },
          });
        }
        case 'DATEPICKER_CHANGE_TAB_SUCCESS': {
          const { tabClickedByUser } = state.datepicker;
          return tabClickedByUser
            ? of(
                {
                  type: 'TOGGLED_SCREEN_ON_DATEPICKER_ANALYTICS',
                  payload,
                },
                { type: 'TAB_CLICKED_BY_USER_RESET' },
              )
            : of();
        }
        case 'CHANGE_DATEPICKER_DATE':
          return payload.value
            ? of({
                type: 'UPDATED_DATEPICKER_ANALYTICS',
                payload,
              })
            : of();
        default:
          return of();
      }
    }),
  );

interface DatepickerPropertiesGeneric {
  datepickerDateType: 'weddingDate';
  datepickerCurrentScreen: string;
  // agreed with Ferg to not use it for now
  datepickerDateValue: Record<string, any>;
}

type DatepickerLocation =
  | 'onboarding'
  | 'settings'
  | 'venueConfirmationPopup'
  | 'enquiry'
  | 'checklist'
  | 'showroundsCalendarModal'
  | null;

const getDatepickerLocation = (getState: () => IApplicationState): DatepickerLocation | null => {
  const {
    venueConfirmation: { showVenueConfirm },
    app: { pathname },
    enquiries: { showEnquiryForm },
    showround: { isOpen: isShowroundsBookingModalOpen },
  } = getState();
  let location: DatepickerLocation = null;
  if (showVenueConfirm) {
    location = 'venueConfirmationPopup';
  } else if (showEnquiryForm) {
    location = 'enquiry';
  } else if (pathname.indexOf('/onboarding') === 0) {
    location = 'onboarding';
  } else if (pathname.indexOf('/settings') === 0) {
    location = 'settings';
  } else if (pathname.indexOf('/checklist') === 0) {
    location = 'checklist';
  } else if (isShowroundsBookingModalOpen) {
    location = 'showroundsCalendarModal';
  }

  return location;
};

export default function datepickerAnalytics(
  { type, payload }: Action,
  bridebookAnalytics: WebAnalyticsContext,
  getState: () => IApplicationState,
) {
  const getDatepicker = () => getState().datepicker;
  const getScreen = (activeTab: string): string => activeTab.replace(/-.*/, '');

  const datepickerPropertiesGeneric = (): DatepickerPropertiesGeneric => {
    const {
      datePickerDate,
      datepicker: { activeTab },
    } = getDatepicker();

    return {
      datepickerDateType: 'weddingDate',
      datepickerCurrentScreen: getScreen(activeTab),
      datepickerDateValue: datePickerDate,
    };
  };
  const updatedDatepickerPropertiesGeneric = () => {
    const { name, value, method } = payload;
    return {
      datepickerField: name,
      datepickerMethod: /.*ndecided.*/.test(name) ? 'checkbox' : method,
      datepickerValue: value,
    };
  };
  const filterLabelProps = (props: Record<string, any>) => {
    const newProps = { ...props };

    delete newProps.datepickerToggledStatus;
    delete newProps.datepickerDateValue;

    return newProps;
  };
  const { track } = bridebookAnalytics.getMethods('Datepicker', filterLabelProps);

  switch (type) {
    case 'TOGGLED_SCREEN_ON_DATEPICKER_ANALYTICS': {
      track({
        event: 'Toggled screen on datepicker',
        ...datepickerPropertiesGeneric(),
        datepickerLocation: getDatepickerLocation(getState),
      });
      break;
    }
    case 'UPDATED_DATEPICKER_ANALYTICS': {
      track({
        event: 'Updated datepicker',
        ...datepickerPropertiesGeneric(),
        ...updatedDatepickerPropertiesGeneric(),
        datepickerLocation: getDatepickerLocation(getState),
      });
      break;
    }
    case 'TOGGLED_DATEPICKER_ANALYTICS': {
      const { datepickerToggledStatus } = payload;
      track({
        event: 'Toggled datepicker',
        ...datepickerPropertiesGeneric(),
        datepickerLocation: getDatepickerLocation(getState),
        datepickerToggledStatus,
      });
      break;
    }
    default:
      break;
  }
}
